/**
 * @file sheets/bottom-sheet/bottom-sheet-scaffold.tsx
 *
 * Standard Bottom Sheet Scaffold — persistent sheet layout component.
 *
 * Mirrors Android Jetpack Compose `BottomSheetScaffold`:
 * - Sheet is always visible at minimum `sheetPeekHeight` (no hidden/dismiss state)
 * - Body content lives above the sheet peek via `paddingBottom`
 * - Optional `topBar` slot
 * - Drag to expand (full container height) / collapse (peek height only)
 * - No scrim (standard sheet, not modal)
 * - Drag handle keyboard accessible: click/Space/Enter toggles state
 *
 * API (Option A — matches Kotlin):
 * ```tsx
 * <BottomSheetScaffold sheetContent={<SheetBody />}>
 *   {(paddingBottom) => <PageBody style={{ paddingBottom }} />}
 * </BottomSheetScaffold>
 * ```
 *
 * @see https://m3.material.io/components/sheets-bottom/overview#standard
 */

import { m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { ScrollArea } from "../../scroll-area";
import { DEFAULT_SPATIAL_SPRING } from "../../shared/motion-tokens";
import { DragHandle } from "../shared/drag-handle";
import type { SheetValue } from "../shared/types";
import { DEFAULT_SHEET_BG } from "../shared/utils";
import { useBottomSheetScaffold } from "./use-bottom-sheet-scaffold";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BottomSheetScaffoldProps {
	/**
	 * Content rendered inside the bottom sheet panel.
	 * Equivalent to Kotlin's `sheetContent: @Composable ColumnScope.() -> Unit`.
	 */
	sheetContent: React.ReactNode;

	/**
	 * Body content of the page. Receives `paddingBottom` string to push
	 * content above the visible sheet peek area.
	 *
	 * @example
	 * ```tsx
	 * {(paddingBottom) => <main style={{ paddingBottom }}>{page}</main>}
	 * ```
	 */
	children: (paddingBottom: string) => React.ReactNode;

	/**
	 * Visible height of the sheet when collapsed.
	 * Default: `"var(--m3-bottom-sheet-peek-height, 56px)"`
	 */
	sheetPeekHeight?: number;

	/**
	 * Maximum width of the sheet.
	 * Default: `"var(--m3-bottom-sheet-max-width, 640px)"`
	 */
	sheetMaxWidth?: string | number;

	/**
	 * Custom drag handle. Pass `null` to hide the handle entirely.
	 * Default: MD3 drag handle indicator bar.
	 */
	sheetDragHandle?: React.ReactNode | null;

	/**
	 * Enable/disable swipe gestures on the sheet.
	 * @default true
	 */
	sheetSwipeEnabled?: boolean;

	/**
	 * Optional top app bar slot rendered above the page body.
	 */
	topBar?: React.ReactNode;

	/**
	 * Background color of the sheet panel.
	 * Defaults to `surfaceContainerLow` token.
	 */
	sheetContainerColor?: string;

	/**
	 * Called when the sheet transitions between states.
	 */
	onSheetStateChange?: (
		state: Extract<SheetValue, "partiallyExpanded" | "expanded">,
	) => void;

	/** Initial state of the sheet. @default "partiallyExpanded" */
	initialSheetState?: Extract<SheetValue, "partiallyExpanded" | "expanded">;

	/** Additional CSS class for the outer scaffold container. */
	className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Standard Bottom Sheet Scaffold.
 *
 * Renders a persistent bottom sheet that stays on screen (never fully hidden).
 * The body content is offset by `sheetPeekHeight` so it is never obscured.
 *
 * @example
 * ```tsx
 * <BottomSheetScaffold
 *   sheetContent={<p>Player controls</p>}
 *   topBar={<AppBar />}
 * >
 *   {(paddingBottom) => (
 *     <AlbumList style={{ paddingBottom }} />
 *   )}
 * </BottomSheetScaffold>
 * ```
 */
export const BottomSheetScaffold = React.forwardRef<
	HTMLDivElement,
	BottomSheetScaffoldProps
>(
	(
		{
			sheetContent,
			children,
			sheetPeekHeight = 56,
			sheetMaxWidth,
			sheetDragHandle,
			sheetSwipeEnabled = true,
			topBar,
			sheetContainerColor,
			onSheetStateChange,
			initialSheetState = "partiallyExpanded",
			className,
		},
		ref,
	) => {
		const {
			y,
			toggle,
			isDragEnabled,
			dragConstraints,
			handleDragEnd,
			handleContentScroll,
			contentPaddingBottom,
			containerRef,
		} = useBottomSheetScaffold({
			peekHeight: sheetPeekHeight,
			swipeEnabled: sheetSwipeEnabled,
			onStateChange: onSheetStateChange,
			initialState: initialSheetState,
		});

		const resolvedSheetMaxWidth = React.useMemo(() => {
			if (sheetMaxWidth === undefined)
				return "var(--m3-bottom-sheet-max-width, 640px)";
			if (typeof sheetMaxWidth === "number") return `${sheetMaxWidth}px`;
			return sheetMaxWidth;
		}, [sheetMaxWidth]);

		const mergedRef = React.useCallback(
			(node: HTMLDivElement | null) => {
				containerRef(node);
				if (typeof ref === "function") ref(node);
				else if (ref) ref.current = node;
			},
			[containerRef, ref],
		);

		// Default drag handle: MD3 indicator bar, interactive for keyboard users
		const resolvedDragHandle = React.useMemo(() => {
			if (sheetDragHandle === null) return null;
			if (sheetDragHandle !== undefined) return sheetDragHandle;
			return <DragHandle onCycle={toggle} />;
		}, [sheetDragHandle, toggle]);

		return (
			<div
				ref={mergedRef}
				className={cn(
					"relative flex flex-col h-full w-full overflow-hidden",
					className,
				)}
			>
				{/* Top bar slot */}
				{topBar && <div className="shrink-0 z-10">{topBar}</div>}

				{/* Body content area — receives paddingBottom to clear the peek area */}
				<div className="relative flex-1 min-h-0 overflow-y-auto">
					{children(contentPaddingBottom)}
				</div>

				{/* Sheet panel — positioned absolutely at the bottom of the scaffold */}
				<m.div
					className={cn(
						"absolute bottom-0 left-0 right-0 mx-auto",
						"rounded-t-[28px] flex flex-col overflow-hidden",
						!sheetContainerColor && DEFAULT_SHEET_BG,
						// MD3 elevation-1 for standard sheet
						"elevation-1",
					)}
					style={{
						backgroundColor: sheetContainerColor,
						maxWidth: resolvedSheetMaxWidth,
						y,
						// Sheet panel is always tall enough to fill from top to bottom
						height: "100%",
						transformOrigin: "center bottom",
					}}
					drag={isDragEnabled ? "y" : false}
					dragConstraints={dragConstraints}
					dragElastic={{ top: 0.1, bottom: 0 }}
					onDragEnd={handleDragEnd}
					transition={DEFAULT_SPATIAL_SPRING}
				>
					{/* Drag handle */}
					{resolvedDragHandle}

					{/* Sheet content in a scroll area */}
					<ScrollArea
						type="hover"
						className="flex-1 min-h-0 min-w-0"
						viewportClassName="px-6 pb-6"
						viewportProps={{
							onScroll: handleContentScroll,
						}}
					>
						{sheetContent}
					</ScrollArea>
				</m.div>
			</div>
		);
	},
);

BottomSheetScaffold.displayName = "BottomSheetScaffold";
